import React, { useEffect, useState } from 'react';
import Breadcrumb from '../../../components/Breadcrumb';
import { createUser, updateUser, getUserById } from '../../../axios/UserAxios';
import Swal from 'sweetalert2';
import { Link, useNavigate, useParams } from 'react-router-dom';
import LoadingOverlay from '../../../components/LoadingOverlay';
import { getBusinessUnitList } from '../../../axios/BusinessUnitAxios';
import Select from 'react-select';

const UsersAdd = () => {
  const navigation = useNavigate();
  const { id } = useParams();

  const [formData, setFormData] = useState({
    user_name: '',
    name: '',
    password: '',
    business_code: '',
    level: '',
  });

  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [changePassword, setChangePassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [businessUnits, setBusinessUnits] = useState([]);

  const [breadcrumbItems, setBreadcrumbItems] = useState([
    { label: 'Dashboard', href: '/' },
    { label: 'Users', href: '/users' },
    { label: 'Create User' },
  ]);

  const businessUnitOptions = businessUnits.map((unit) => ({
    value: unit.business_code,
    label: `${unit.business_code} - ${unit.name}`,
  }));

  useEffect(() => {
    if (id) {
      setBreadcrumbItems([
        { label: 'Dashboard', href: '/' },
        { label: 'Users', href: '/users' },
        { label: 'Update User' },
      ]);
    }
  }, [id]);

  useEffect(() => {
    if (id) {
      const fetchUserData = async () => {
        try {
          setMessage('Fetching data...');
          setLoading(true);

          const response = await getUserById(id);

          setFormData({
            user_name: response.user_name ?? '',
            name: response.name ?? '',
            password: '',
            business_code: response.business_code ?? '',
            level: response.level ?? '',
          });
        } catch (error) {
          console.error('Error fetching user data:', error);
        } finally {
          setLoading(false);
        }
      };

      fetchUserData();
    }
  }, [id]);

  useEffect(() => {
    const fetchBusinessUnits = async () => {
      try {
        const response = await getBusinessUnitList();

        setBusinessUnits(response?.data || []);
        console.log(businessUnits);
      } catch (error) {
        console.error('Error fetching business units:', error);
      }
    };

    fetchBusinessUnits();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const form = e.target;

    if (!form.checkValidity()) {
      form.classList.add('was-validated');
      return;
    }

    const payload = { ...formData };
    if (id && !changePassword) {
      delete payload.password;
    }

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1',
        denyButton: 'btn btn-danger mx-1',
        cancelButton: 'btn btn-secondary mx-1',
      },
      buttonsStyling: false,
    });

    try {
      const result = await swalWithBootstrapButtons.fire({
        title: 'Do you want to save the data?',
        showDenyButton: true,
        showCancelButton: true,
        confirmButtonText: 'Save',
        cancelButtonText: `Don't save`,
      });

      if (result.isConfirmed) {
        try {
          setMessage('Saving data...');
          setLoading(true);

          if (id) {
            await updateUser(id, payload);
            swalWithBootstrapButtons.fire(
              'Saved!',
              'The user has been updated.',
              'success'
            );
          } else {
            await createUser(formData);
            swalWithBootstrapButtons.fire(
              'Saved!',
              'The user has been created.',
              'success'
            );
          }

          navigation('/users');
        } catch (error) {
          swalWithBootstrapButtons.fire({
            title: 'Error!',
            text: error.message,
            icon: 'error',
          });
        } finally {
          setLoading(false);
        }
      } else if (result.isDenied) {
        swalWithBootstrapButtons.fire('Changes are not saved', '', 'info');
      }
    } catch (error) {
      swalWithBootstrapButtons.fire({
        title: 'Error!',
        text: 'There was an error processing your request.',
        icon: 'error',
      });
      console.error(error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const formElements = Array.from(e.target.form.elements);
      const index = formElements.indexOf(e.target);
      formElements[index + 1]?.focus();
    }
  };

  const handleCancel = (e) => {
    e.preventDefault();

    const swalWithBootstrapButtons = Swal.mixin({
      customClass: {
        confirmButton: 'btn btn-primary mx-1',
        cancelButton: 'btn btn-secondary mx-1',
      },
      buttonsStyling: false,
    });

    swalWithBootstrapButtons
      .fire({
        title: 'Are you sure?',
        text: 'All your input will be lost!',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, cancel it!',
        cancelButtonText: 'No, keep it',
      })
      .then((result) => {
        if (result.isConfirmed) {
          const form = document.querySelector('.needs-validation');
          form.reset();

          setFormData((prev) => ({
            user_name: id ? prev.user_name : '',
            name: '',
            password: '',
            business_code: '',
            level: '',
          }));

          setChangePassword(false);
          setShowPassword(false);

          form.classList.remove('was-validated');
        }
      });
  };

  return (
    <div>
      <LoadingOverlay show={loading} message={message} />
      <Breadcrumb items={breadcrumbItems} />

      <form
        className="needs-validation"
        noValidate
        autoComplete="off"
        onSubmit={handleSubmit}
      >
        <div className="card">
          <div className="card-body">
            {/* Username */}
            <div className="mb-3">
              <label className="form-label">Username</label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="user_name"
                value={formData.user_name}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
                disabled={!!id} // opsional: username tidak bisa diubah
              />
              <div className="invalid-feedback">Cannot be empty.</div>
            </div>

            {/* Name */}
            <div className="mb-3">
              <label className="form-label">Name</label>
              <input
                type="text"
                className="form-control form-control-sm"
                name="name"
                value={formData.name}
                onChange={handleChange}
                onKeyDown={handleKeyDown}
                required
              />
              <div className="invalid-feedback">Cannot be empty.</div>
            </div>

            {/* Toggle Change Password */}
            {id && (
              <div className="form-check mb-2">
                <input
                  className="form-check-input"
                  type="checkbox"
                  checked={changePassword}
                  onChange={(e) => {
                    setChangePassword(e.target.checked);
                    if (!e.target.checked) {
                      setFormData((prev) => ({ ...prev, password: '' }));
                      setShowPassword(false);
                    }
                  }}
                />

                <label className="form-check-label">Change Password</label>
              </div>
            )}

            {/* Password */}
            {(!id || changePassword) && (
              <div className="mb-3">
                <label className="form-label">Password</label>

                <div className="input-group input-group-sm">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    className="form-control"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required={!id || changePassword}
                    placeholder={id ? 'Leave blank if not changing' : ''}
                  />

                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => setShowPassword(!showPassword)}
                    tabIndex={-1}
                  >
                    <i
                      className={`bi ${
                        showPassword ? 'bi-eye-slash' : 'bi-eye'
                      }`}
                    ></i>
                  </button>
                </div>
              </div>
            )}

            {/* Business Code */}
            <div className="mb-3">
              <label className="form-label">Business Code</label>

              <Select
                classNamePrefix="react-select"
                options={businessUnitOptions}
                value={businessUnitOptions.find(
                  (opt) => opt.value === formData.business_code
                )}
                onChange={(selected) =>
                  setFormData((prev) => ({
                    ...prev,
                    business_code: selected ? selected.value : '',
                  }))
                }
                placeholder="Search business unit..."
                isClearable
                isDisabled={!!id}
              />

              {!formData.business_code && (
                <div
                  className="text-danger mt-1"
                  style={{ fontSize: '0.875em' }}
                >
                  Cannot be empty.
                </div>
              )}
            </div>

            {/* Level */}
            <div className="mb-3">
              <label className="form-label">Level</label>
              <select
                className="form-select form-select-sm"
                name="level"
                value={formData.level}
                onChange={handleChange}
                required
              >
                <option value="">Choose...</option>
                <option value="1">Operator Proyek</option>
                <option value="4">TEM</option>
                <option value="7">Daily</option>
                <option value="9">Admin</option>
              </select>
              <div className="invalid-feedback">Cannot be empty.</div>
            </div>

            {/* Buttons */}
            <div className="d-flex gap-2">
              <button type="submit" className="btn btn-primary">
                <i className="bi bi-send"></i> Submit
              </button>
              <button
                type="reset"
                className="btn btn-danger"
                onClick={handleCancel}
              >
                <i className="bi bi-x-circle"></i> Cancel
              </button>
              <Link className="btn btn-warning" to="/users">
                <i className="bi bi-arrow-left-circle"></i> Back
              </Link>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default UsersAdd;
